import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Typography } from "@mui/material";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import setAuthToken from "services/modules/utils/handel_api";
import { useThemePalette } from "common/hooks/theme_palette";
import MeritOrdersTemplate from "templates/merit/orders";
import {
  getMeritOrderDetailsRequest,
  getMeritOrdersRequest,
} from "services/modules/merit";
import {
  buildMeritListParams,
  getCustomerEmail,
  getCustomerName,
  getGiftCardName,
  getOrderAmount,
  getOrderReference,
} from "templates/merit/utils";

const STATUS_COLORS = {
  completed: "success",
  success: "success",
  delivered: "success",
  pending: "warning",
  processing: "warning",
  failed: "error",
  error: "error",
  canceled: "error",
  cancelled: "error",
};

function MeritOrdersPage() {
  const { t } = useTranslation();
  const { backgroundGreen, backgroundRed, textGreen, textRed, orange } =
    useThemePalette();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.merit.orders);
  const count = useSelector((state) => state.merit.ordersCount);
  const details = useSelector((state) => state.merit.orderDetails);
  const load = useSelector((state) => state.merit.load);

  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    setFilterObj((prev) => ({
      ...prev,
      page_number: nextPage,
    }));
  };

  const getDetails = () => {
    dispatch(
      getMeritOrderDetailsRequest({
        id: selectedRow?.rowData?.id ?? id,
      }),
    );
  };

  const getList = (filters) => {
    dispatch(
      getMeritOrdersRequest({
        params: buildMeritListParams(filters),
      }),
    );
  };

  const renderStatusBadge = (status) => {
    const normalized = String(status ?? "-").toLowerCase();
    const tone = STATUS_COLORS[normalized];
    const isSuccess = tone === "success";
    const isError = tone === "error";

    return (
      <Typography
        sx={{
          bgcolor: isSuccess
            ? backgroundGreen
            : isError
              ? backgroundRed
              : orange,
          color: isSuccess ? textGreen : isError ? textRed : "white",
          p: "1px 10px",
          width: "fit-content",
          borderRadius: "4px",
          textTransform: "capitalize",
        }}
      >
        {status ?? "-"}
      </Typography>
    );
  };

  const mappedData = list?.map((data) => ({
    ...data,
    reference: getOrderReference(data),
    amount_display: (
      <>
        {getOrderAmount(data)} {t("products.curr_sar")}
      </>
    ),
    customer_name_display: getCustomerName(data),
    customer_email_display: getCustomerEmail(data),
    gift_card_display: getGiftCardName(data),
    status_display: renderStatusBadge(data?.status),
    date: (
      <>
        {data?.created_at
          ? dayjs(data.created_at).format("DD-MM-YYYY, hh a")
          : "-"}
      </>
    ),
  }));

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type,
      path: "/merit/orders",
      title: t("merit_orders.title"),
    },
    type === "details" && {
      active: false,
      path: "",
      title: t("merit_orders.order_details"),
    },
  ].filter(Boolean);

  const filterList = [
    {
      type: "search",
      placeholder: t("merit_orders.search_placeholder"),
      onChange: (value) =>
        setFilterObj((prev) => ({
          ...prev,
          search: value?.trim?.() ?? value,
          page_number: 1,
        })),
    },
    {
      type: "filter",
      placeholder: t("merit_orders.filter_status"),
      options: [
        { id: "", name: t("merit_orders.all_statuses") },
        { id: "pending", name: t("merit_orders.status_pending") },
        { id: "processing", name: t("merit_orders.status_processing") },
        { id: "completed", name: t("merit_orders.status_completed") },
        { id: "failed", name: t("merit_orders.status_failed") },
      ],
      onChange: (value) =>
        setFilterObj((prev) => ({
          ...prev,
          status: value?.value || undefined,
          page_number: 1,
        })),
    },
  ];

  const actionsList = ({ data }) => [
    {
      condition: () => true,
      click: () => navigate(`?type=details&id=${data?.id}`),
      text: t("common.view"),
    },
  ];

  useEffect(() => {
    getList(filterObj || {});
  }, [filterObj]);

  useEffect(() => {
    if (id && pathname?.includes("merit/orders")) {
      getDetails();
    }
  }, [id, pathname]);

  return (
    <>
      <Loader open={load?.loading && load?.type === "list orders"} />
      <MeritOrdersTemplate
        data={mappedData}
        count={count}
        details={details}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        actionsList={actionsList}
        setFilterObj={setFilterObj}
      />
    </>
  );
}

export default MeritOrdersPage;
